import { useState, useEffect, useMemo, useCallback } from 'react';
import { useAuth } from '@/context/AuthContext';
import { NavLink } from 'react-router-dom';
import { 
  Rocket, 
  Globe,
  Mail,
  MessageCircle,
  Target,
  CheckCircle2,
  AlertCircle,
  ExternalLink,
  Copy,
  Download,
  RefreshCw,
  Loader2,
  AlertTriangle,
  Check,
  Send,
  Eye,
  ChevronDown,
  ChevronUp,
  Facebook,
  Play,
  Pause,
  Clock,
  Link2,
  LayoutDashboard
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';

// Deployment status badges
const STATUS_CONFIG = {
  pending: { label: 'Pendiente', color: 'bg-gray-500/20 text-gray-400', icon: Clock },
  deploying: { label: 'Desplegando...', color: 'bg-blue-500/20 text-blue-400', icon: Loader2 },
  success: { label: 'Publicado', color: 'bg-green-500/20 text-green-400', icon: CheckCircle2 },
  error: { label: 'Error', color: 'bg-red-500/20 text-red-400', icon: AlertCircle },
  skipped: { label: 'Omitido', color: 'bg-amber-500/20 text-amber-400', icon: AlertTriangle }
};

// Status Badge Component
const StatusBadge = ({ status }) => {
  const config = STATUS_CONFIG[status] || STATUS_CONFIG.pending;
  const Icon = config.icon;
  
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${config.color}`}>
      <Icon className={`w-3 h-3 ${status === 'deploying' ? 'animate-spin' : ''}`} />
      {config.label}
    </span>
  );
};

// Collapsible Section Component
const DeploySection = ({ 
  title, 
  icon: Icon, 
  iconColor, 
  count, 
  status,
  children, 
  defaultOpen = false,
  disabled = false,
  comingSoon = false
}) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  
  return (
    <div className={`rounded-2xl border transition-all ${
      disabled ? 'border-border-subtle/50 opacity-60' : 'border-border-subtle hover:border-brand/30'
    }`}>
      <button
        onClick={() => !disabled && setIsOpen(!isOpen)}
        disabled={disabled}
        className="w-full p-5 flex items-center justify-between text-left"
        data-testid={`deploy-section-${title.toLowerCase().replace(/\s+/g, '-')}`}
      >
        <div className="flex items-center gap-4">
          <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${iconColor}`}>
            <Icon className="w-6 h-6" />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <h3 className="font-semibold text-text-main">{title}</h3>
              {count > 0 && (
                <span className="px-2 py-0.5 text-xs bg-brand/20 text-brand-400 rounded-full">
                  {count} elemento{count !== 1 ? 's' : ''}
                </span>
              )}
              {comingSoon && (
                <span className="px-2 py-0.5 text-xs bg-amber-500/20 text-amber-400 rounded-full">
                  Próximamente
                </span>
              )}
            </div>
            <p className="text-sm text-text-muted mt-0.5">
              {status ? <StatusBadge status={status} /> : 'Configurar deployment'}
            </p>
          </div>
        </div>
        
        {!disabled && (
          isOpen ? <ChevronUp className="w-5 h-5 text-text-muted" /> : <ChevronDown className="w-5 h-5 text-text-muted" />
        )}
      </button>
      
      {isOpen && !disabled && (
        <div className="px-5 pb-5 pt-0 border-t border-border-subtle/50">
          {children}
        </div>
      )}
    </div>
  );
};

// Landing Item Component
const LandingItem = ({ landing, onDeploy, deploying, deployResult }) => (
  <div className="p-4 bg-surface rounded-xl border border-border-subtle">
    <div className="flex items-start justify-between gap-4">
      <div className="flex-1 min-w-0">
        <h4 className="font-medium text-text-main truncate">{landing.title || landing.name || 'Landing Page'}</h4>
        <p className="text-sm text-text-muted mt-1 truncate">{landing.slug || landing.type || 'optin'}</p>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        {deployResult?.success ? (
          <a 
            href={deployResult.url} 
            target="_blank" 
            rel="noopener noreferrer"
            className="text-brand hover:text-brand-hover text-sm flex items-center gap-1"
          >
            <ExternalLink className="w-4 h-4" />
            Ver
          </a>
        ) : (
          <Button
            size="sm"
            onClick={() => onDeploy(landing)}
            disabled={deploying}
            className="bg-brand hover:bg-brand-hover"
          >
            {deploying ? <Loader2 className="w-4 h-4 animate-spin" /> : <Rocket className="w-4 h-4" />}
            <span className="ml-1.5">{deploying ? 'Publicando...' : 'Publicar'}</span>
          </Button>
        )}
      </div>
    </div>
    {deployResult?.error && (
      <p className="text-xs text-red-400 mt-2">{deployResult.error}</p>
    )}
    {deployResult?.success && deployResult.url && (
      <div className="mt-2 p-2 bg-green-500/10 rounded-lg">
        <p className="text-xs text-green-400 flex items-center gap-1">
          <CheckCircle2 className="w-3 h-3" />
          Publicado: {deployResult.url}
        </p>
      </div>
    )}
  </div>
);

// Email Sequence Item Component
const EmailItem = ({ email, index, onDeploy, deploying }) => (
  <div className="p-4 bg-surface rounded-xl border border-border-subtle">
    <div className="flex items-start justify-between gap-4">
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="w-6 h-6 rounded-full bg-brand/20 text-brand-400 text-xs flex items-center justify-center font-medium">
            {index + 1}
          </span>
          <h4 className="font-medium text-text-main truncate">{email.subject || email.asunto || `Email ${index + 1}`}</h4>
        </div>
        <p className="text-sm text-text-muted mt-1 line-clamp-2">{email.preview || email.preview_text || ''}</p>
      </div>
      <Button
        size="sm"
        variant="outline"
        onClick={() => onDeploy(email)}
        disabled={deploying}
      >
        <Send className="w-4 h-4 mr-1.5" />
        Enviar
      </Button>
    </div>
  </div>
);

// Ad Creative Item Component  
const AdItem = ({ ad, platform, onDeploy, deploying, deployResult }) => (
  <div className="p-4 bg-surface rounded-xl border border-border-subtle">
    <div className="flex items-start gap-4">
      {ad.creativo_feed_url && (
        <div className="w-20 h-20 rounded-lg overflow-hidden bg-app-bg shrink-0">
          <img 
            src={ad.creativo_feed_url} 
            alt={ad.headline || 'Ad'} 
            className="w-full h-full object-cover"
          />
        </div>
      )}
      <div className="flex-1 min-w-0">
        <h4 className="font-medium text-text-main truncate">{ad.headline || `Anuncio ${ad.id}`}</h4>
        <p className="text-sm text-text-muted mt-1 truncate">{ad.formato || '4:5'} · {ad.motor || 'flux_pro'}</p>
        {deployResult?.success && (
          <p className="text-xs text-green-400 mt-1">ID: {deployResult.ad_id}</p>
        )}
      </div>
      <StatusBadge status={deployResult?.success ? 'success' : deployResult?.error ? 'error' : 'pending'} />
    </div>
  </div>
);


const CampaignsStep = ({ funnel, brief, assets, assetsConfig }) => {
  const { api, user } = useAuth();
  
  // Integration choices from Step 6
  const [integrationChoices, setIntegrationChoices] = useState(null);
  const [connections, setConnections] = useState({
    meta: false,
    google: false,
    ghl: false,
    wordpress: false
  });
  
  // Deployment states per section
  const [deployStates, setDeployStates] = useState({
    landings: {}, // { landingId: { status: 'pending'|'deploying'|'success'|'error', url?: string, error?: string } }
    emails: {},
    ads: {},
    whatsapp: {}
  });
  
  // Loading states
  const [loadingConnections, setLoadingConnections] = useState(true);
  const [deployingLanding, setDeployingLanding] = useState(null);
  const [deployingEmail, setDeployingEmail] = useState(null);
  const [deployingAds, setDeployingAds] = useState(false);
  
  // Content from funnel - handle both array and dict shapes
  const landings = useMemo(() => {
    const raw = funnel?.landings;
    if (!raw) return [];
    if (Array.isArray(raw)) return raw;
    // Convert dict to array with id from key
    return Object.entries(raw).map(([key, value]) => ({ 
      ...value, 
      id: value.id || key, 
      slug: value.slug || key 
    }));
  }, [funnel?.landings]);
  
  const emails = useMemo(() => {
    const raw = funnel?.sequences?.emails || funnel?.assets?.emails;
    if (!raw) return [];
    if (Array.isArray(raw)) return raw;
    return Object.values(raw);
  }, [funnel]);
  
  const whatsappMessages = useMemo(() => {
    const raw = funnel?.sequences?.whatsapp;
    if (!raw) return [];
    if (Array.isArray(raw)) return raw;
    return Object.values(raw);
  }, [funnel]);
  
  const adCopies = useMemo(() => {
    const raw = funnel?.adCopies || funnel?.assets?.adsCopyCaptacion;
    if (!raw) return [];
    if (Array.isArray(raw)) return raw;
    return Object.values(raw);
  }, [funnel]);
  
  const creativos = useMemo(() => {
    const raw = funnel?.creativos;
    if (!raw) return [];
    if (Array.isArray(raw)) return raw;
    return Object.values(raw);
  }, [funnel]);
  
  // Load integration choices and connection status
  useEffect(() => {
    const loadIntegrations = async () => {
      if (!funnel?.id) return;
      
      setLoadingConnections(true);
      try {
        // Get funnel data with integration choices
        const funnelRes = await api.get(`/funnels/${funnel.id}`);
        const funnelData = funnelRes.data;
        
        if (funnelData?.integration_choices) {
          setIntegrationChoices(funnelData.integration_choices);
        }
        
        // Check actual connection statuses using CANONICAL endpoints (not stale funnel object)
        const [metaRes, googleRes, ghlRes, wpRes] = await Promise.all([
          api.get('/integrations/meta/status', { params: { funnel_id: funnel.id } }).catch(() => ({ data: {} })),
          api.get('/integrations/google-ads/status', { params: { funnel_id: funnel.id } }).catch(() => ({ data: {} })),
          api.get('/integrations/crm/status', { params: { funnel_id: funnel.id } }).catch(() => ({ data: {} })),
          api.get('/integrations/wordpress/status', { params: { funnel_id: funnel.id } }).catch(() => ({ data: {} }))
        ]);
        
        setConnections({
          meta: metaRes.data?.connected || false,
          google: googleRes.data?.connected || false,
          ghl: ghlRes.data?.connected || false,
          wordpress: wpRes.data?.connected || false
        });
        
        console.log('[CampaignsStep] Connection status:', {
          meta: metaRes.data?.connected,
          google: googleRes.data?.connected,
          ghl: ghlRes.data?.connected,
          wordpress: wpRes.data?.connected
        });
        
        // Load existing deploy states from funnel
        if (funnelData?.deploy_states) {
          setDeployStates(funnelData.deploy_states);
        }
        
      } catch (err) {
        console.error('Failed to load integrations:', err);
      } finally {
        setLoadingConnections(false);
      }
    };
    
    loadIntegrations();
  }, [api, funnel?.id]);
  
  // Save deploy states to MongoDB
  const saveDeployStates = useCallback(async (newStates) => {
    try {
      await api.put(`/funnels/${funnel.id}`, {
        deploy_states: newStates
      });
    } catch (err) {
      console.error('Failed to save deploy states:', err);
    }
  }, [api, funnel?.id]);
  
  // Deploy landing to WordPress/GHL
  const handleDeployLanding = async (landing) => {
    const landingId = landing.id || landing.slug;
    setDeployingLanding(landingId);
    
    const newStates = { ...deployStates };
    newStates.landings[landingId] = { status: 'deploying' };
    setDeployStates(newStates);
    
    try {
      let result;
      
      // Check connection status fresh from backend before deploying
      const [ghlRes, wpRes] = await Promise.all([
        api.get('/integrations/crm/status', { params: { funnel_id: funnel.id } }).catch(() => ({ data: {} })),
        api.get('/integrations/wordpress/status', { params: { funnel_id: funnel.id } }).catch(() => ({ data: {} }))
      ]);
      
      const ghlConnected = ghlRes.data?.connected;
      const wpConnected = wpRes.data?.connected;
      
      // Determine which platform to use based on integration_choices and actual connections
      const webChoice = integrationChoices?.web;
      
      if (ghlConnected && (webChoice === 'ghl' || !wpConnected)) {
        // Deploy to GoHighLevel using CONSOLIDATED endpoint
        result = await api.post('/integrations/crm/deploy-landings', {
          funnel_id: funnel.id,
          landing_id: landingId,
          landing_html: landing.html || landing.content,
          landing_name: landing.title || landing.name,
          landing_slug: landing.slug || landingId
        });
      } else if (wpConnected) {
        // Deploy to WordPress
        result = await api.post('/integrations/wordpress/deploy-landing', {
          funnel_id: funnel.id,
          landing_id: landingId,
          landing_html: landing.html || landing.content,
          landing_title: landing.title || landing.name,
          landing_slug: landing.slug || landingId
        });
      } else {
        throw new Error('No hay plataforma web conectada. Conecta WordPress o GoHighLevel en el paso de Integraciones.');
      }
      
      if (result.data?.success) {
        newStates.landings[landingId] = { 
          status: 'success', 
          url: result.data.url,
          deployed_at: new Date().toISOString()
        };
        toast.success('Landing publicada correctamente', {
          description: result.data.url ? `URL: ${result.data.url}` : undefined
        });
      } else {
        throw new Error(result.data?.message || result.data?.detail || 'Error al publicar');
      }
    } catch (err) {
      const errorMsg = err.response?.data?.detail || err.response?.data?.message || err.message;
      newStates.landings[landingId] = { 
        status: 'error', 
        error: errorMsg 
      };
      toast.error('Error al publicar landing', { description: errorMsg });
    } finally {
      setDeployStates(newStates);
      saveDeployStates(newStates);
      setDeployingLanding(null);
    }
  };
  
  // Deploy email sequence to GHL/Email platform
  const handleDeployEmail = async (email) => {
    const emailId = email.id || email.subject;
    setDeployingEmail(emailId);
    
    const newStates = { ...deployStates };
    newStates.emails[emailId] = { status: 'deploying' };
    setDeployStates(newStates);
    
    try {
      let result;
      
      // Check GHL connection status fresh from backend
      const ghlRes = await api.get('/integrations/crm/status', { params: { funnel_id: funnel.id } }).catch(() => ({ data: {} }));
      const ghlConnected = ghlRes.data?.connected;
      
      if (ghlConnected) {
        // Deploy to GoHighLevel using CONSOLIDATED endpoint
        result = await api.post('/integrations/crm/deploy-emails', {
          funnel_id: funnel.id,
          email_id: emailId,
          email_subject: email.subject || email.asunto,
          email_html: email.html || email.content,
          email_preview: email.preview || email.preview_text
        });
      } else {
        // Export as downloadable HTML
        const blob = new Blob([email.html || email.content], { type: 'text/html' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `email-${emailId}.html`;
        a.click();
        
        newStates.emails[emailId] = { status: 'success', downloaded: true };
        toast.success('Email descargado como HTML');
        setDeployStates(newStates);
        saveDeployStates(newStates);
        setDeployingEmail(null);
        return;
      }
      
      if (result.data?.success) {
        newStates.emails[emailId] = { 
          status: 'success', 
          deployed_at: new Date().toISOString() 
        };
        toast.success('Email enviado a la secuencia');
      } else {
        throw new Error(result.data?.message || 'Error al enviar');
      }
    } catch (err) {
      const errorMsg = err.response?.data?.detail || err.response?.data?.message || err.message;
      newStates.emails[emailId] = { 
        status: 'error', 
        error: errorMsg 
      };
      toast.error('Error al desplegar email', { description: errorMsg });
    } finally {
      setDeployStates(newStates);
      saveDeployStates(newStates);
      setDeployingEmail(null);
    }
  };
  
  // Deploy ads to Meta/Google
  const handleDeployAds = async (platform) => {
    setDeployingAds(true);
    
    try {
      // Check connection status fresh from backend before deploying
      const [metaRes, googleRes] = await Promise.all([
        api.get('/integrations/meta/status', { params: { funnel_id: funnel.id } }).catch(() => ({ data: {} })),
        api.get('/integrations/google-ads/status', { params: { funnel_id: funnel.id } }).catch(() => ({ data: {} }))
      ]);
      
      const metaConnected = metaRes.data?.connected;
      const googleConnected = googleRes.data?.connected;
      
      if (platform === 'meta') {
        if (!metaConnected) {
          throw new Error('Meta Ads no está conectado. Conecta tu cuenta en el paso de Integraciones.');
        }
        
        // Get account info - check both meta_ads and meta keys
        const funnelRes = await api.get(`/funnels/${funnel.id}`);
        const funnelData = funnelRes.data;
        const metaConfig = funnelData?.client_integrations?.meta_ads || funnelData?.client_integrations?.meta || {};
        const metaAccount = metaConfig.selected_ad_account || metaConfig.ad_account_id;
        
        if (!metaAccount) {
          throw new Error('Selecciona una cuenta publicitaria de Meta primero');
        }
        
        // Get selected creatives or use all
        const creativesPayload = creativos.map(c => ({
          image_url: c.creativo_feed_url,
          title: c.headline || adCopies[0]?.headline || 'Tu Oferta',
          body: adCopies[0]?.texto_principal || brief?.offer_text || 'Descubre más',
          link_url: landings[0]?.url || 'https://example.com',
          call_to_action: 'LEARN_MORE'
        }));
        
        const result = await api.post(`/integrations/meta/deploy-full-campaign`, {
          funnel_id: funnel.id,
          ad_account_id: metaAccount,
          campaign_name: `${funnel.name || 'FASCINAI'} - Meta Campaign`,
          objective: 'OUTCOME_LEADS',
          daily_budget: 1000, // 10€ in cents
          countries: ['ES'],
          age_min: 25,
          age_max: 55,
          optimization_goal: 'LANDING_PAGE_VIEWS',
          billing_event: 'IMPRESSIONS',
          creatives: creativesPayload.length > 0 ? creativesPayload : [{
            title: adCopies[0]?.headline || 'Tu Oferta Irresistible',
            body: adCopies[0]?.texto_principal || 'Descubre cómo podemos ayudarte',
            link_url: landings[0]?.url || 'https://example.com',
            call_to_action: 'LEARN_MORE'
          }],
          landing_url: landings[0]?.url || 'https://example.com'
        });
        
        if (result.data?.success) {
          const newStates = { ...deployStates };
          newStates.ads.meta = { 
            status: 'success', 
            campaign_id: result.data.campaign?.id,
            deployed_at: new Date().toISOString() 
          };
          setDeployStates(newStates);
          saveDeployStates(newStates);
          toast.success('Campaña de Meta Ads creada en PAUSADO');
        } else {
          throw new Error(result.data?.message || 'Error al crear campaña');
        }
      } else if (platform === 'google') {
        if (!googleConnected) {
          throw new Error('Google Ads no está conectado. Conecta tu cuenta en el paso de Integraciones.');
        }
        
        // Get account info
        const funnelRes = await api.get(`/funnels/${funnel.id}`);
        const funnelData = funnelRes.data;
        const googleConfig = funnelData?.client_integrations?.google_ads || {};
        const customerId = googleConfig.customer_id || googleConfig.selected_customer_id;
        const managerId = googleConfig.manager_id;
        
        if (!customerId) {
          throw new Error('Selecciona una cuenta de Google Ads primero');
        }
        
        const result = await api.post('/integrations/google-ads/deploy-full-campaign', {
          funnel_id: funnel.id,
          customer_id: customerId,
          manager_id: managerId,  // Include manager_id for multi-tenant support
          campaign_name: `${funnel.name || 'FASCINAI'} - Google Campaign`,
          campaign_type: 'SEARCH',
          daily_budget_micros: 10000000, // 10€ in micros
          countries: ['ES'],
          languages: ['es'],
          keywords: brief?.keywords?.split(',').map(k => k.trim()) || [brief?.product_name || 'curso online'],
          headlines: adCopies.slice(0, 5).map(c => c.headline || c.frase).filter(Boolean),
          descriptions: adCopies.slice(0, 2).map(c => c.texto_principal).filter(Boolean),
          final_url: landings[0]?.url || 'https://example.com'
        });
        
        if (result.data?.success) {
          const newStates = { ...deployStates };
          newStates.ads.google = { 
            status: 'success', 
            campaign_id: result.data.campaign?.name,
            deployed_at: new Date().toISOString() 
          };
          setDeployStates(newStates);
          saveDeployStates(newStates);
          toast.success('Campaña de Google Ads creada en PAUSADO');
        } else {
          throw new Error(result.data?.message || 'Error al crear campaña');
        }
      }
    } catch (err) {
      toast.error('Error al desplegar anuncios', { description: err.response?.data?.detail || err.message });
    } finally {
      setDeployingAds(false);
    }
  };
  
  // Copy all content to clipboard
  const handleCopyAll = (section) => {
    let content = '';
    
    if (section === 'emails') {
      content = emails.map((e, i) => `--- EMAIL ${i + 1}: ${e.subject || e.asunto} ---\n${e.content || e.html}`).join('\n\n');
    } else if (section === 'whatsapp') {
      content = whatsappMessages.map((m, i) => `--- MENSAJE ${i + 1} ---\n${m.content || m.texto}`).join('\n\n');
    }
    
    navigator.clipboard.writeText(content);
    toast.success('Contenido copiado al portapapeles');
  };
  
  // Calculate overall deployment status
  const overallStatus = useMemo(() => {
    const landingStatuses = Object.values(deployStates.landings || {});
    const emailStatuses = Object.values(deployStates.emails || {});
    const adStatuses = Object.values(deployStates.ads || {});
    
    const allStatuses = [...landingStatuses, ...emailStatuses, ...adStatuses];
    
    if (allStatuses.length === 0) return 'pending';
    if (allStatuses.some(s => s.status === 'deploying')) return 'deploying';
    if (allStatuses.every(s => s.status === 'success')) return 'success';
    if (allStatuses.some(s => s.status === 'error')) return 'error';
    return 'pending';
  }, [deployStates]);
  
  // Determine which sections to show based on integration choices
  const showLandings = landings.length > 0 && (connections.ghl || connections.wordpress || integrationChoices?.web);
  const showEmails = emails.length > 0;
  const showAds = (adCopies.length > 0 || creativos.length > 0) && (connections.meta || connections.google);
  const showWhatsApp = whatsappMessages.length > 0;
  
  if (loadingConnections) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-8 h-8 animate-spin text-brand" />
      </div>
    );
  }
  
  return (
    <div className="space-y-6" data-testid="campaigns-step">
      {/* Header */}
      <div className="p-6 rounded-2xl bg-gradient-to-r from-brand/10 to-purple-500/10 border border-brand/30">
        <div className="flex items-center gap-4">
          <div className="w-14 h-14 rounded-2xl bg-brand/20 flex items-center justify-center">
            <Rocket className="w-7 h-7 text-brand" />
          </div>
          <div className="flex-1">
            <h2 className="text-xl font-bold text-text-main">Centro de Lanzamiento</h2>
            <p className="text-sm text-text-muted mt-1">
              Despliega tu funnel completo: landings, emails y anuncios desde un solo lugar.
            </p>
          </div>
          <StatusBadge status={overallStatus} />
        </div>
        
        {/* Quick stats */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mt-6">
          <div className="p-3 rounded-xl bg-surface/50">
            <Globe className="w-5 h-5 text-blue-400 mb-1" />
            <p className="text-lg font-semibold text-text-main">{landings.length}</p>
            <p className="text-xs text-text-muted">Landings</p>
          </div>
          <div className="p-3 rounded-xl bg-surface/50">
            <Mail className="w-5 h-5 text-green-400 mb-1" />
            <p className="text-lg font-semibold text-text-main">{emails.length}</p>
            <p className="text-xs text-text-muted">Emails</p>
          </div>
          <div className="p-3 rounded-xl bg-surface/50">
            <Target className="w-5 h-5 text-purple-400 mb-1" />
            <p className="text-lg font-semibold text-text-main">{creativos.length || adCopies.length}</p>
            <p className="text-xs text-text-muted">Creativos/Ads</p>
          </div>
          <div className="p-3 rounded-xl bg-surface/50">
            <MessageCircle className="w-5 h-5 text-amber-400 mb-1" />
            <p className="text-lg font-semibold text-text-main">{whatsappMessages.length}</p>
            <p className="text-xs text-text-muted">WhatsApp</p>
          </div>
        </div>
      </div>
      
      {/* Integration Status Banner */}
      {(!connections.meta && !connections.google && !connections.ghl && !connections.wordpress) && (
        <div className="p-4 rounded-xl bg-amber-500/10 border border-amber-500/30">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-amber-400 shrink-0 mt-0.5" />
            <div>
              <p className="text-sm text-amber-400 font-medium">Sin integraciones conectadas</p>
              <p className="text-xs text-text-muted mt-1">
                Vuelve al paso anterior (Integraciones) para conectar tus plataformas de publicidad, web o email.
                Mientras tanto, puedes descargar el contenido manualmente.
              </p>
            </div>
          </div>
        </div>
      )}
      
      {/* Deployment Sections */}
      <div className="space-y-4">
        
        {/* LANDINGS SECTION */}
        <DeploySection
          title="Landing Pages"
          icon={Globe}
          iconColor="bg-blue-500/20 text-blue-400"
          count={landings.length}
          status={Object.values(deployStates.landings || {}).some(s => s.status === 'success') ? 'success' : 
                  Object.values(deployStates.landings || {}).some(s => s.status === 'error') ? 'error' : 'pending'}
          defaultOpen={showLandings}
          disabled={landings.length === 0}
        >
          <div className="pt-4 space-y-4">
            {/* Platform indicator */}
            {(connections.ghl || connections.wordpress) && (
              <div className="flex items-center gap-2 text-sm text-green-400 mb-4">
                <CheckCircle2 className="w-4 h-4" />
                Conectado a: {connections.ghl ? 'GoHighLevel' : 'WordPress'}
              </div>
            )}
            
            {/* Landing list */}
            {landings.length > 0 ? (
              <div className="space-y-3">
                {landings.map((landing, idx) => (
                  <LandingItem
                    key={landing.id || landing.slug || `landing-${idx}`}
                    landing={landing}
                    onDeploy={handleDeployLanding}
                    deploying={deployingLanding === (landing.id || landing.slug)}
                    deployResult={deployStates.landings?.[landing.id || landing.slug]}
                  />
                ))}
              </div>
            ) : (
              <p className="text-sm text-text-muted">No hay landings generadas. Vuelve al paso de Landings para crearlas.</p>
            )}
            
            {/* Bulk actions */}
            {landings.length > 1 && (
              <Button
                variant="outline"
                className="w-full mt-4"
                onClick={() => landings.forEach(l => handleDeployLanding(l))}
                disabled={deployingLanding !== null}
              >
                <Rocket className="w-4 h-4 mr-2" />
                Publicar todas las landings
              </Button>
            )}
          </div>
        </DeploySection>
        
        {/* EMAILS SECTION */}
        <DeploySection
          title="Secuencia de Emails"
          icon={Mail}
          iconColor="bg-green-500/20 text-green-400"
          count={emails.length}
          status={Object.values(deployStates.emails || {}).some(s => s.status === 'success') ? 'success' : 'pending'}
          defaultOpen={showEmails && !showLandings}
          disabled={emails.length === 0}
        >
          <div className="pt-4 space-y-4">
            {connections.ghl && (
              <div className="flex items-center gap-2 text-sm text-green-400 mb-4">
                <CheckCircle2 className="w-4 h-4" />
                Los emails se enviarán vía GoHighLevel
              </div>
            )}
            
            {emails.length > 0 ? (
              <div className="space-y-3">
                {emails.map((email, idx) => (
                  <EmailItem
                    key={email.id || `email-${idx}`}
                    email={email}
                    index={idx}
                    onDeploy={handleDeployEmail}
                    deploying={deployingEmail === (email.id || email.subject)}
                  />
                ))}
              </div>
            ) : (
              <p className="text-sm text-text-muted">No hay emails generados.</p>
            )}
            
            {/* Copy all button */}
            {emails.length > 0 && (
              <Button
                variant="outline"
                className="w-full mt-4"
                onClick={() => handleCopyAll('emails')}
              >
                <Copy className="w-4 h-4 mr-2" />
                Copiar todos los emails
              </Button>
            )}
          </div>
        </DeploySection>
        
        {/* ADS SECTION */}
        <DeploySection
          title="Campañas de Anuncios"
          icon={Target}
          iconColor="bg-purple-500/20 text-purple-400"
          count={creativos.length || adCopies.length}
          status={deployStates.ads?.meta?.status || deployStates.ads?.google?.status || 'pending'}
          defaultOpen={showAds}
          disabled={!showAds}
        >
          <div className="pt-4 space-y-4">
            {/* Meta Ads */}
            {connections.meta && (
              <div className="p-4 rounded-xl bg-blue-500/10 border border-blue-500/30">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Facebook className="w-6 h-6 text-blue-400" />
                    <div>
                      <h4 className="font-medium text-text-main">Meta Ads</h4>
                      <p className="text-xs text-text-muted">
                        {deployStates.ads?.meta?.status === 'success' 
                          ? `Campaña ID: ${deployStates.ads.meta.campaign_id}` 
                          : 'Facebook & Instagram'}
                      </p>
                    </div>
                  </div>
                  {deployStates.ads?.meta?.status === 'success' ? (
                    <a 
                      href={`https://business.facebook.com/adsmanager`} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-blue-400 hover:text-blue-300 text-sm flex items-center gap-1"
                    >
                      <ExternalLink className="w-4 h-4" /> Ver en Ads Manager
                    </a>
                  ) : (
                    <Button
                      onClick={() => handleDeployAds('meta')}
                      disabled={deployingAds}
                      className="bg-blue-500 hover:bg-blue-600"
                    >
                      {deployingAds ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Rocket className="w-4 h-4 mr-2" />}
                      Crear Campaña
                    </Button>
                  )}
                </div>
              </div>
            )}
            
            {/* Google Ads */}
            {connections.google && (
              <div className="p-4 rounded-xl bg-gradient-to-r from-green-500/10 to-yellow-500/10 border border-green-500/30">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-6 h-6 flex items-center justify-center text-lg">🔍</div>
                    <div>
                      <h4 className="font-medium text-text-main">Google Ads</h4>
                      <p className="text-xs text-text-muted">
                        {deployStates.ads?.google?.status === 'success' 
                          ? `Campaña: ${deployStates.ads.google.campaign_id}` 
                          : 'Search, Display & YouTube'}
                      </p>
                    </div>
                  </div>
                  {deployStates.ads?.google?.status === 'success' ? (
                    <a 
                      href={`https://ads.google.com`} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-green-400 hover:text-green-300 text-sm flex items-center gap-1"
                    >
                      <ExternalLink className="w-4 h-4" /> Ver en Google Ads
                    </a>
                  ) : (
                    <Button
                      onClick={() => handleDeployAds('google')}
                      disabled={deployingAds}
                      className="bg-gradient-to-r from-green-500 to-yellow-500 text-white"
                    >
                      {deployingAds ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Rocket className="w-4 h-4 mr-2" />}
                      Crear Campaña
                    </Button>
                  )}
                </div>
              </div>
            )}
            
            {/* No ads platforms connected */}
            {!connections.meta && !connections.google && (
              <div className="p-4 rounded-lg bg-surface text-center">
                <Target className="w-8 h-8 text-text-muted mx-auto mb-2" />
                <p className="text-sm text-text-muted">
                  Conecta Meta Ads o Google Ads en el paso de Integraciones para desplegar campañas automáticamente.
                </p>
                <p className="text-xs text-text-muted mt-2">
                  Mientras tanto, puedes descargar tus creativos desde el paso de Creativos.
                </p>
              </div>
            )}
          </div>
        </DeploySection>
        
        {/* WHATSAPP SECTION */}
        <DeploySection
          title="Mensajes WhatsApp"
          icon={MessageCircle}
          iconColor="bg-amber-500/20 text-amber-400"
          count={whatsappMessages.length}
          defaultOpen={false}
          disabled={whatsappMessages.length === 0}
          comingSoon={true}
        >
          <div className="pt-4 space-y-4">
            <div className="p-4 rounded-lg bg-amber-500/10 border border-amber-500/30">
              <p className="text-sm text-amber-400">
                La automatización de WhatsApp llegará próximamente. Mientras tanto, puedes copiar los mensajes generados.
              </p>
            </div>
            
            {whatsappMessages.length > 0 && (
              <>
                <div className="space-y-3">
                  {whatsappMessages.slice(0, 3).map((msg, idx) => (
                    <div key={`wa-${idx}`} className="p-4 bg-surface rounded-xl border border-border-subtle">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="w-6 h-6 rounded-full bg-green-500/20 text-green-400 text-xs flex items-center justify-center font-medium">
                          {idx + 1}
                        </span>
                        <span className="text-sm font-medium text-text-main">Mensaje {idx + 1}</span>
                      </div>
                      <p className="text-sm text-text-muted line-clamp-3">{msg.content || msg.texto}</p>
                    </div>
                  ))}
                </div>
                
                {whatsappMessages.length > 3 && (
                  <p className="text-xs text-text-muted text-center">
                    + {whatsappMessages.length - 3} mensajes más
                  </p>
                )}
                
                <Button
                  variant="outline"
                  className="w-full"
                  onClick={() => handleCopyAll('whatsapp')}
                >
                  <Copy className="w-4 h-4 mr-2" />
                  Copiar todos los mensajes
                </Button>
              </>
            )}
          </div>
        </DeploySection>
      </div>
      
      {/* Help text */}
      <div className="p-4 rounded-xl bg-surface border border-border-subtle">
        <div className="flex items-start gap-3">
          <LayoutDashboard className="w-5 h-5 text-brand shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-medium text-text-main">¿Necesitas configurar más integraciones?</p>
            <p className="text-xs text-text-muted mt-1">
              Vuelve al paso anterior (Integraciones) para conectar más plataformas. 
              Los cambios se reflejarán aquí automáticamente.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CampaignsStep;
